export type NyeriMetode = "nrs" | "flacc" | "nips" | "bps";
export type NyeriBand = "tidak" | "ringan" | "sedang" | "berat";

export interface IntervensiNyeri {
  band: NyeriBand;
  title: string;
  color: string;
  img: string;
  text: string;
}

const AMBANG: Record<
  NyeriMetode,
  { ringan: number; sedang: number; berat: number }
> = {
  nrs: { ringan: 3, sedang: 6, berat: 10 },
  flacc: { ringan: 3, sedang: 6, berat: 10 },
  nips: { ringan: 3, sedang: 5, berat: 7 },
  bps: { ringan: 5, sedang: 8, berat: 12 }
};

const ALAT: Record<NyeriMetode, string> = {
  nrs: "VAS",
  flacc: "FLACC",
  nips: "NIPS",
  bps: "BPS"
};

const SISIPAN_RINGAN: Record<NyeriMetode, string> = {
  nrs: " c",
  flacc: "",
  nips: " c",
  bps: " c"
};

const KONSUL: Record<NyeriMetode, string> = {
  nrs: "DPJP mengkonsulkan",
  flacc: "DPJP mengkonsulkan",
  nips: "Mengkonsulkan",
  bps: "DPJP mengkonsulkan"
};

const IMG: Record<NyeriMetode, Record<NyeriBand, string>> = {
  nrs: {
    tidak: "/img/wbs/wbs0.png",
    ringan: "/img/wbs/wbs1.png",
    sedang: "/img/wbs/wbs2.png",
    berat: "/img/wbs/wbs3.png"
  },
  flacc: { tidak: "", ringan: "", sedang: "", berat: "" },
  nips: { tidak: "", ringan: "", sedang: "", berat: "" },
  bps: { tidak: "", ringan: "", sedang: "", berat: "" }
};

const JUDUL: Record<NyeriBand, string> = {
  tidak: "Tidak Ada Nyeri",
  ringan: "Nyeri Ringan",
  sedang: "Nyeri Sedang",
  berat: "Nyeri Berat"
};

const WARNA: Record<NyeriBand, string> = {
  tidak: "black",
  ringan: "green",
  sedang: "orange",
  berat: "red"
};

function teks(metode: NyeriMetode, band: NyeriBand): string {
  const alat = ALAT[metode];
  switch (band) {
    case "tidak":
      return `
        1. Pantau skala nyeri pasien menggunakan pengukuran ${alat}<br/>
        2. Jika ditemukan tidak ada nyeri/ tidak ada keluhan nyeri maka tuliskan pada kolom pemantauan dengan angka 0<br/>
        3. Kaji kembali skala nyeri pasien setiap 8 jam atau setiap pergantian shift dinas
        `;
    case "ringan":
      return `
        1. Pantau skala nyeri pasien menggunakan pengukuran ${alat} <br/>
        2. Jika ditemukan pasien ada keluhan sesuai dengan kategori penilaian nilai/score ${alat}${SISIPAN_RINGAN[metode]} ringan maka tuliskan angka pada kolom pemantauan <br/>
        3. Berikan intervensi keperawatan dengan teknik nafas dalam atau terapi non farmakologis <br/>
        4. Lakukan observasi kondisi pasien <br/>
        5. Kaji kembali skala nyeri pasien setiap 8 jam atau setiap pergantian shift dinas
        `;
    case "sedang":
      return `
        1. Pantau skala nyeri pasien menggunakan pengukuran ${alat} <br/>
        2. Jika ditemukan pasien ada keluhan sesuai dengan kategori penilaian nilai/ score ${alat} sedang, maka tuliskan angka pada kolom pemantauan <br/>
        3. Berikan intervensi keperawatan dengan teknik nafas dalam atau terapi non farmakologis <br/>
        4. Laporkan ke dokter penanggung jawab pasien dpjp untuk diberikan tatalaksana nyeri farmakologis <br/>
        5. Kaji kembali skala nyeri pasien setiap 2 jam <br/>
        6. Bila terapi farmakologis sudah diberikan skala nyeri masih belum berkurang maka laporkan kembali DP JP <br/>
        7. ${KONSUL[metode]} ke tim manajemen nyeri
        `;
    default:
      return `
        1. Pantau skala nyeri pasien menggunakan pengukuran ${alat} <br/>
        2. Jika ditemukan pasien ada keluhan sesuai dengan kategori penilaian nilai/skor ${alat} berat maka tuliskan angka pada kolom pemantauan <br/>
        3. Lakukan protokol nyeri sedang <br/>
        4. Tim manajemen nyeri mengelola dengan obat- obat sedatif <br/>
        5. Kaji kembali skala nyeri pasien setiap 1 jam
        `;
  }
}

export function kodeMetodeNyeri(raw: unknown): NyeriMetode | null {
  const k = String(raw || "")
    .trim()
    .toLowerCase();
  return k === "nrs" || k === "flacc" || k === "nips" || k === "bps"
    ? (k as NyeriMetode)
    : null;
}

export function bandNyeri(metode: unknown, skala: unknown): NyeriBand | null {
  if (skala === null || skala === undefined || skala === "") return null;
  const s = Number(skala);
  if (isNaN(s)) return null;

  const kode = kodeMetodeNyeri(metode) || "nrs";
  const a = AMBANG[kode];

  if (s === 0) return "tidak";
  if (s <= a.ringan) return "ringan";
  if (s <= a.sedang) return "sedang";
  if (s <= a.berat) return "berat";
  return null;
}

export function judulBandNyeri(band: NyeriBand | null): string {
  return band ? JUDUL[band] : "-";
}

export function warnaBandNyeri(band: NyeriBand | null): string {
  return band ? WARNA[band] : "grey";
}

export function intervensiNyeri(
  metode: unknown,
  skala: unknown
): IntervensiNyeri | null {
  const kode = kodeMetodeNyeri(metode);
  if (!kode) return null;
  const band = bandNyeri(kode, skala);
  if (!band) return null;

  return {
    band,
    title: JUDUL[band],
    color: WARNA[band],
    img: IMG[kode][band],
    text: teks(kode, band)
  };
}
